import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import { backendService } from "../../../services/backend.service";
import { CAREERS_QUERY_KEY } from "../consts";

export type CareersAccess = {
	/** The caller may use Careers in the chosen department. Always false while `loading`. */
	allowed: boolean;
	/**
	 * The answer is not known yet: on a cold load the chosen department is picked in an effect AFTER the first
	 * render (until then `FeatureFlagsProvider` has no department to ask about), and with the flag on, the backend
	 * is still being asked about the caller's hiring role there.
	 */
	loading: boolean;
};

/** Roles change rarely (a recruiter added in Team); a few minutes of reuse keeps every page from asking again. */
const ACCESS_STALE_MS = 5 * 60 * 1000;

/**
 * Whether the caller gets Careers in the chosen department: the `ats_careers` flag is on there, and they are a
 * recruiter or interviewer in it, or support — the Hiring Hub's rule, which the backend enforces on every `/careers`
 * route. The flag is read here first, so the many users it is off for never wait on a request; with it on, the
 * role is the backend's answer (`GET /careers/access`), so the tab, the route guard and the root redirect can never
 * offer a page whose every request would 403. A failed check shows no Careers (it is tracked, not snackbarred).
 */
export const useCareersAccess = (): CareersAccess => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const isFlagOn = useFeatureFlag(FEATURE_FLAGS.AtsCareers);
	const roleCheck = useQuery({
		queryKey: [CAREERS_QUERY_KEY.ACCESS, departmentId] as const,
		queryFn: async () => (await backendService.fetchCareersAccess(departmentId)).data.allowed,
		enabled: isFlagOn && departmentId !== "",
		staleTime: ACCESS_STALE_MS,
		throwOnError: (error: unknown) => {
			trackError("careers_access_fetch_error", { department_id: departmentId, error_message: error instanceof Error ? error.message : String(error) });
			return false;
		},
	});

	// A user with no department never gets one chosen, so there is nothing to wait for — just no access.
	// Optional chaining as in `ChoseDepartmentProvider`: userInfo can still be the pre-fetch placeholder.
	if (departmentId === "") return { allowed: false, loading: !!userInfo.departments?.length };
	if (!isFlagOn) return { allowed: false, loading: false };
	if (roleCheck.isPending) return { allowed: false, loading: true };
	return { allowed: roleCheck.data === true, loading: false };
};
